import { Controller } from '@hotwired/stimulus';

/**
 * Toggles Ui::InvoicePreviewModalComponent between "normal" (iframe + form)
 * and "annotation" (PDF.js canvas + Annotations::PanelComponent) sub-trees.
 *
 * Targets:
 * - normalRoot — the normal-mode 2-column grid (preview + info/form panel)
 * - annotationRoot — the annotation-mode 2-column grid; also hosts data-controller="annotation-viewer"
 * - toggleButton — the footer "Annoter" button
 *
 * Cross-controller bridge (2026-05-16):
 *   Listens for `jerico-insight:show-annotations` on `window`. Triggered by
 *   the Jerico Insight modal's "Voir les annotations sur la facture" CTA
 *   (see `jerico_insight_controller.js#openOnFacture`). On receipt, enters
 *   annotation mode if not already there. The `detail.page` is currently
 *   informational only — the annotation-viewer renders all pages by default
 *   and the user can scroll. A future iteration may surface a public
 *   `scrollPageIntoView` action on the annotation-viewer controller.
 */
export default class extends Controller {
  static targets = [
    'normalRoot',
    'annotationRoot',
    'toggleButton',
    'createSituationButton',
    'rejectButton',
  ];

  connect() {
    this._onShowAnnotations = this._onShowAnnotations.bind(this);
    window.addEventListener('jerico-insight:show-annotations', this._onShowAnnotations);
  }

  disconnect() {
    window.removeEventListener('jerico-insight:show-annotations', this._onShowAnnotations);
  }

  toggle() {
    if (!this.hasAnnotationRootTarget) return;
    if (this.annotationRootTarget.classList.contains('hidden')) {
      this.enter();
    } else {
      this.exit();
    }
  }

  // Public entry point — same behavior as the private `#enter()` used to
  // have. Renamed so external triggers (the show-annotations event) can
  // request annotation mode idempotently.
  enter() {
    if (!this.hasAnnotationRootTarget) return;
    if (!this.annotationRootTarget.classList.contains('hidden')) return; // already entered
    this.normalRootTarget.classList.add('hidden');
    this.annotationRootTarget.classList.remove('hidden');
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.add('bg-warning-100', 'text-warning-700');
    }
    // Bug 2 — annotation_viewer connected with hidden parent (0×0 canvas).
    // Notify it now that the parent is laid out so it can re-render pins.
    this.annotationRootTarget.dispatchEvent(
      new CustomEvent('annotation:refresh', { bubbles: false })
    );
    if (this.hasCreateSituationButtonTarget) {
      this.createSituationButtonTarget.style.display = 'none';
    }
    if (this.hasRejectButtonTarget) {
      const btn = this.rejectButtonTarget;
      const labelEl = btn.querySelector('span') || btn;
      btn.dataset.originalLabel = labelEl.textContent;
      labelEl.textContent = btn.dataset.labelActive || labelEl.textContent;
    }
  }

  exit() {
    if (!this.hasAnnotationRootTarget) return;
    this.normalRootTarget.classList.remove('hidden');
    this.annotationRootTarget.classList.add('hidden');
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.remove('bg-warning-100', 'text-warning-700');
    }
    if (this.hasCreateSituationButtonTarget) {
      this.createSituationButtonTarget.style.display = '';
    }
    if (this.hasRejectButtonTarget) {
      const btn = this.rejectButtonTarget;
      const labelEl = btn.querySelector('span') || btn;
      labelEl.textContent =
        btn.dataset.labelDefault || btn.dataset.originalLabel || labelEl.textContent;
    }
  }

  _onShowAnnotations(_event) {
    // Multiple InvoicePreviewModalComponent instances may be rendered per page
    // (e.g., dashboard/listing pages with N pending invoices). The window event
    // reaches all of them; only the visible one should respond. `this.element`
    // is the outer modal — it carries the `hidden` class while closed.
    if (this.element.classList.contains('hidden')) return;
    // `enter()` is idempotent (no-op if already entered), so safe to call blindly.
    // `event.detail.page` is currently unused — see header docblock.
    this.enter();
  }
}
